import {
  HARDWARE_FAMILIES,
  type AudienceTarget,
  type AudienceTargetKind,
  type AudienceTargetOption,
  type AudienceTargets,
  type HardwareFamily,
  type HeadsetRelease,
  type HeadsetReleaseStatus,
  type InstallMode,
  type Progress,
} from "types/headsetReleaseTypes";

export const MODE_COLORS: Record<InstallMode, string> = {
  available: "gray",
  auto: "green",
  forced: "red",
};

export const STATUS_COLORS: Record<HeadsetReleaseStatus, string> = {
  installed: "green",
  available: "blue",
  auto: "teal",
  forced: "red",
  waiting: "gray",
  skipped: "gray",
};

export function percentOf({ done, total }: Progress): number {
  return total ? Math.round((done / total) * 100) : 0;
}

export function targetOptions(
  kind: AudienceTargetKind,
  targets: AudienceTargets,
): AudienceTargetOption[] {
  if (kind === "distributor") return targets.distributors;
  if (kind === "accountType") return targets.accountTypes;
  return targets.orgs;
}

export function targetName(target: AudienceTarget, targets?: AudienceTargets): string {
  const option = targets && targetOptions(target.kind, targets).find(({ id }) => id === target.id);
  return option?.name ?? target.id;
}

export function targetOrgCount(
  target: AudienceTarget,
  targets?: AudienceTargets,
): number | undefined {
  if (target.kind === "org") return 1;
  if (!targets) return undefined;
  return targetOptions(target.kind, targets).find(({ id }) => id === target.id)?.orgCount;
}

export function minorLineOf(version: string): string {
  return version.split(".").slice(0, 2).join(".");
}

export function familiesWithoutApk(
  release: Pick<HeadsetRelease, "hardware" | "apks">,
): HardwareFamily[] {
  return release.hardware.filter((family) => !release.apks.some((apk) => apk.family === family));
}

export function withFamily(
  hardware: HardwareFamily[],
  family: HardwareFamily,
  selected: boolean,
): HardwareFamily[] {
  return HARDWARE_FAMILIES.filter((other) =>
    other === family ? selected : hardware.includes(other),
  );
}

export function canBeLatest(
  release: Pick<HeadsetRelease, "isLatest" | "status" | "hardware" | "apks">,
) {
  return release.isLatest || (release.status === "started" && hasEveryApk(release));
}

export function hasEveryApk(release: Pick<HeadsetRelease, "hardware" | "apks">): boolean {
  return release.apks.length > 0 && familiesWithoutApk(release).length === 0;
}
